// SLEEPSAKE: mystery-offer card game over a Klaviyo sign-up step.
//
// The welcome popup (Klaviyo form Xa5aK7) A/B tests today's 10% offer against
// a "mystery offer". Version B is one plain Klaviyo step — a Custom HTML block
// holding `<div class="ss-mg-game"></div>`, the "You've unlocked a mystery
// offer" heading, the email field and Klaviyo's own Claim button. This module
// covers that step with four face-down cards; turning any three removes the
// cover so Klaviyo's step shows and Klaviyo does the sign-up itself. Nothing
// here ever submits, so the A/B test still measures Klaviyo's own submits.
//
// FAIL SAFE BY CONSTRUCTION. The cover is the only thing we add that hides
// anything, and every path that cannot finish it removes it: no marker, an
// exception, the stylesheet not applied yet, storage blocked. The worst case
// is always "Klaviyo's step as Klaviyo built it" (on version B's sky, a
// look class that is purely decoration). Klaviyo's close button is a sibling
// of the <form> at z-index 6; the cover sits inside the form below it.
//
// The state helpers are pure and exported so `theme-tests/mystery-cards.test.mjs`
// pins them under `node --test`; `theme-tests/e2e/mystery-cards.spec.ts` drives
// the DOM against a fake Klaviyo form.

// The storage key is a contract with every visitor's browser: changing it
// silently resets their turned cards (and re-locks anyone who already
// unlocked). Never rename it; make parseState tolerate the new shape instead.
export const STORAGE_KEY = "ss-mystery-cards-v1";
export const CARD_COUNT = 4;
export const CARDS_TO_UNLOCK = 3;
export const REVEAL_DELAY_MS = 600;
export const MARKER_SELECTOR = ".ss-mg-game";

const COVER_CLASS = "ss-mc";
const LOOK_CLASS = "ss-mc-look";
const LOOK_HOST_CLASS = "ss-mc-look-host";
const BODY_WATCH_MS = 1500;
const CSS_TIMED_RETRIES = 5;
const CSS_RETRY_MS = 400;
const MARKER_TEXT = "unlockedamysteryoffer";

/** @typedef {{ turned: number[], unlocked: boolean }} CardsState */

/** @returns {CardsState} */
export function emptyState() {
  return { turned: [], unlocked: false };
}

/**
 * Reads a stored state, keeping whatever still makes sense and dropping the
 * rest, so an older or hand-edited value never throws or clears progress.
 *
 * @param {string | null | undefined} raw the stored JSON, or nothing
 * @returns {CardsState} turned indexes are unique integers in [0, CARD_COUNT),
 *   in turn order; `unlocked` is true once CARDS_TO_UNLOCK are turned
 */
export function parseState(raw) {
  if (typeof raw !== "string" || raw === "") return emptyState();
  let data;
  try {
    data = JSON.parse(raw);
  } catch {
    return emptyState();
  }
  if (!data || typeof data !== "object") return emptyState();
  const turned = [];
  for (const value of Array.isArray(data.turned) ? data.turned : []) {
    if (Number.isInteger(value) && value >= 0 && value < CARD_COUNT && !turned.includes(value)) turned.push(value);
  }
  return { turned, unlocked: data.unlocked === true || turned.length >= CARDS_TO_UNLOCK };
}

/** @param {CardsState} state */
export function serializeState(state) {
  return JSON.stringify({ turned: state.turned, unlocked: state.unlocked });
}

/**
 * Turns one card. Cards never turn back, and nothing turns once unlocked.
 *
 * @param {CardsState} state
 * @param {number} index card index in [0, CARD_COUNT)
 * @returns {CardsState} a new state, or the same object when nothing changed
 */
export function turnCard(state, index) {
  if (state.unlocked || !Number.isInteger(index) || index < 0 || index >= CARD_COUNT) return state;
  if (state.turned.includes(index)) return state;
  const turned = [...state.turned, index];
  return { turned, unlocked: turned.length >= CARDS_TO_UNLOCK };
}

/**
 * Whether a piece of form text names the mystery-offer step. Whitespace is
 * dropped before matching because Klaviyo splits a heading across rich-text
 * blocks ("You've unlocked a" / "mystery offer") with no space between them.
 *
 * @param {string | null | undefined} text
 */
export function textNamesMysteryStep(text) {
  return typeof text === "string" && text.replace(/\s+/g, "").toLowerCase().includes(MARKER_TEXT);
}

// Preview switch for playing the game before version B exists in Klaviyo:
// `?ss-mystery-demo=1` treats any Klaviyo popup as version B for the rest of
// the tab (sessionStorage), `=0` turns it off, `=reset` turns it on and also
// clears the saved cards so the game replays from four face-down cards.
export const DEMO_PARAM = "ss-mystery-demo";
export const DEMO_SESSION_KEY = "ss-mystery-demo";

/**
 * @param {string} search a location.search string, with or without "?"
 * @returns {"on" | "off" | "reset" | null} null when the switch is absent or
 *   carries any other value, which leaves the tab's current setting alone
 */
export function demoSwitch(search) {
  let value;
  try {
    value = new URLSearchParams(search).get(DEMO_PARAM);
  } catch {
    return null;
  }
  if (value === "1") return "on";
  if (value === "0") return "off";
  if (value === "reset") return "reset";
  return null;
}

/** @param {Storage | undefined} storage */
export function readState(storage) {
  try {
    return parseState(storage?.getItem(STORAGE_KEY));
  } catch {
    return emptyState();
  }
}

/**
 * @param {Storage | undefined} storage
 * @param {CardsState} state
 */
export function writeState(storage, state) {
  try {
    storage?.setItem(STORAGE_KEY, serializeState(state));
  } catch {
    // Private mode or blocked storage: the game still works for this visit.
  }
}

/* ---------------------------------------------------------------- runtime */

function install(win) {
  const doc = win.document;
  if (win.__ssMysteryCards) return;
  win.__ssMysteryCards = true;

  let storage;
  try {
    storage = win.localStorage;
  } catch {
    storage = undefined;
  }
  // Mirrors storage so a visitor whose storage is blocked keeps progress for
  // this page view instead of re-reading an empty state on every sync.
  let session;
  try {
    session = win.sessionStorage;
  } catch {
    session = undefined;
  }
  const demoAction = demoSwitch(win.location?.search || "");
  let demo = demoAction === "on" || demoAction === "reset";
  try {
    if (demoAction === "reset") storage?.removeItem(STORAGE_KEY);
    if (demo) session?.setItem(DEMO_SESSION_KEY, "1");
    else if (demoAction === "off") session?.removeItem(DEMO_SESSION_KEY);
    else demo = session?.getItem(DEMO_SESSION_KEY) === "1";
  } catch {
    // Blocked storage: the switch still applies to this page view.
  }
  let memoryState = readState(storage);
  const loadState = () => {
    const stored = readState(storage);
    memoryState = stored.turned.length >= memoryState.turned.length || stored.unlocked ? stored : memoryState;
    return memoryState;
  };
  const saveState = (state) => {
    memoryState = state;
    writeState(storage, state);
  };

  const observers = new WeakMap();
  let scheduled = false;
  /** @type {"unknown" | "waiting" | "missing"} */
  let cssState = "unknown";
  let cssTimedRetries = 0;

  const klaviyoForms = () => doc.querySelectorAll('form.klaviyo-form, form[data-testid^="klaviyo-form-"]');

  const stepText = (form) => {
    let text = "";
    for (const child of form.children) {
      if (!child.classList.contains(COVER_CLASS)) text += " " + child.textContent;
    }
    return text;
  };

  const isMysteryStep = (form) =>
    (demo && !!form.closest('[role="dialog"]')) ||
    !!form.querySelector(MARKER_SELECTOR) ||
    textNamesMysteryStep(stepText(form));

  const coverOf = (form) => {
    for (const child of form.children) if (child.classList.contains(COVER_CLASS)) return child;
    return null;
  };

  function setStepHidden(form, hidden) {
    for (const child of form.children) {
      if (child.classList.contains(COVER_CLASS)) continue;
      if (hidden) {
        if (!child.hasAttribute("inert")) {
          child.setAttribute("inert", "");
          child.setAttribute("aria-hidden", "true");
          child.dataset.ssMcHidden = "1";
        }
      } else if (child.dataset.ssMcHidden) {
        child.removeAttribute("inert");
        child.removeAttribute("aria-hidden");
        delete child.dataset.ssMcHidden;
      }
    }
  }

  function uncover(form, { focusEmail = false } = {}) {
    try {
      coverOf(form)?.remove();
    } finally {
      setStepHidden(form, false);
      if (form.dataset.ssMcPosition !== undefined) {
        form.style.position = form.dataset.ssMcPosition;
        delete form.dataset.ssMcPosition;
      }
      if (form.dataset.ssMcMinHeight !== undefined) {
        form.style.minHeight = form.dataset.ssMcMinHeight;
        delete form.dataset.ssMcMinHeight;
      }
    }
    if (focusEmail) {
      const email = form.querySelector('input[type="email"], input[name="email"]');
      try {
        email?.focus({ preventScroll: true });
      } catch {
        // Focus is a nicety; the step is already visible.
      }
    }
  }

  function cardLabel(index, turned) {
    return `Card ${index + 1} of ${CARD_COUNT}, ${turned ? "Mystery offer" : "face down"}`;
  }

  function paintCard(button, index, turned, locked) {
    button.classList.toggle("is-turned", turned);
    button.setAttribute("aria-label", cardLabel(index, turned));
    if (turned || locked) button.setAttribute("aria-disabled", "true");
  }

  function buildCover(form, state) {
    const cover = doc.createElement("div");
    cover.className = COVER_CLASS;

    const title = doc.createElement("h2");
    title.className = "ss-mc__title";
    title.textContent = "Try your luck";
    const sub = doc.createElement("p");
    sub.className = "ss-mc__sub";
    sub.textContent = "Match 3 cards to see what you win.";
    const cards = doc.createElement("div");
    cards.className = "ss-mc__cards";
    cards.setAttribute("role", "group");
    cards.setAttribute("aria-label", "Mystery cards");

    const buttons = [];
    for (let index = 0; index < CARD_COUNT; index++) {
      const button = doc.createElement("button");
      // type="button" matters: a bare <button> inside Klaviyo's <form> would submit it.
      button.type = "button";
      button.className = "ss-mc__card";
      button.dataset.index = String(index);
      const flip = doc.createElement("span");
      flip.className = "ss-mc__flip";
      flip.setAttribute("aria-hidden", "true");
      const back = doc.createElement("span");
      back.className = "ss-mc__face ss-mc__face--down";
      back.textContent = "?";
      const front = doc.createElement("span");
      front.className = "ss-mc__face ss-mc__face--up";
      front.textContent = "Mystery offer";
      flip.append(back, front);
      button.append(flip);
      paintCard(button, index, state.turned.includes(index), false);
      button.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        onTurn(form, cover, buttons, index);
      });
      buttons.push(button);
    }
    cards.append(...buttons);

    const inner = doc.createElement("div");
    inner.className = "ss-mc__inner";
    inner.append(title, sub, cards);
    cover.append(inner);
    return cover;
  }

  function onTurn(form, cover, buttons, index) {
    try {
      if (cover.dataset.revealing) return;
      const before = loadState();
      // Another tab finished the game while this cover was up.
      if (before.unlocked) {
        uncover(form, { focusEmail: true });
        return;
      }
      const after = turnCard(before, index);
      if (after === before) return;
      saveState(after);
      paintCard(buttons[index], index, true, false);
      if (!after.unlocked) return;
      cover.dataset.revealing = "1";
      buttons.forEach((button, i) => paintCard(button, i, after.turned.includes(i), true));
      win.setTimeout(() => uncover(form, { focusEmail: true }), REVEAL_DELAY_MS);
    } catch {
      uncover(form);
    }
  }

  function cover(form, state) {
    if (win.getComputedStyle(form).position === "static") {
      form.dataset.ssMcPosition = form.style.position;
      form.style.position = "relative";
    }
    const node = buildCover(form, state);
    form.append(node);
    // The stylesheet loads without blocking render (snippets/scripts.liquid
    // flips its media to "all" on load); until it applies, the cover would
    // sit unstyled under the step, so back off and retry once it loads. If it
    // never applies, the step stays as Klaviyo built it.
    //
    // WebKit re-fetches the sheet when the onload handler flips its media, so
    // the first load event can arrive with media already "all" and the sheet
    // still not applied, then a second load follows. So every miss waits for
    // the next load, and a few timed retries cover a load that never fires.
    if (win.getComputedStyle(node).position === "static") {
      uncover(form);
      const link = doc.getElementById("ss-mystery-cards-css");
      if (link && (link.media !== "all" || cssTimedRetries < CSS_TIMED_RETRIES)) {
        cssState = "waiting";
        let retried = false;
        const retry = () => {
          if (retried) return;
          retried = true;
          cssState = "unknown";
          scheduleSync();
        };
        link.addEventListener("load", retry, { once: true });
        if (link.media === "all") {
          cssTimedRetries++;
          win.setTimeout(retry, CSS_RETRY_MS);
        }
      } else {
        cssState = "missing";
      }
      return;
    }
    setStepHidden(form, true);
    fitFormToCover(form, node);
    const firstDown = node.querySelector(".ss-mc__card:not(.is-turned)");
    try {
      firstDown?.focus({ preventScroll: true });
    } catch {
      // Focus is a nicety.
    }
  }

  // A short step (a heading, an email field, a button) can be shorter than
  // the game, so grow the form rather than let the cover scroll inside itself.
  // The cover centres its content, which pushes overflow off BOTH edges and
  // hides half of it from scrollHeight, so measure the content directly.
  // offsetHeight is layout size, unaffected by Klaviyo's transform: scale().
  //
  // Runs again on every sync, because Klaviyo can fire its events before the
  // popup is laid out, when everything measures 0. On phones the cover sits
  // in normal flow (see the stylesheet), so the form sizes itself and this
  // stays out of the way.
  function fitFormToCover(form, node) {
    const inner = node.firstElementChild;
    const style = win.getComputedStyle(node);
    if (!inner || style.position !== "absolute" || !inner.offsetHeight) return;
    const needed = inner.offsetHeight + parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);
    if (needed <= node.clientHeight) return;
    const formBorders = form.offsetHeight - form.clientHeight;
    if (form.dataset.ssMcMinHeight === undefined) form.dataset.ssMcMinHeight = form.style.minHeight;
    form.style.minHeight = `${Math.ceil(needed + formBorders)}px`;
  }

  // Version B's sky and corner pillow. It stays on the form for every later
  // step (the claim step after the cards, Klaviyo's success step), so the
  // popup never flips back to white mid-flow. The image URLs come from the
  // stylesheet link and are only set here, so pages where version B never
  // shows never fetch them.
  function applyLook(form) {
    const host = form.parentElement;
    if (!form.classList.contains(LOOK_CLASS)) {
      const link = doc.getElementById("ss-mystery-cards-css");
      const sky = link?.dataset.sky;
      const pillow = link?.dataset.pillow;
      if (sky) form.style.setProperty("--ss-mc-sky", `url("${sky}")`);
      if (pillow) form.style.setProperty("--ss-mc-pillow", `url("${pillow}")`);
      form.classList.add(LOOK_CLASS);
      host?.classList.add(LOOK_HOST_CLASS);
    }
    // Klaviyo shrinks the whole popup with transform: scale() on phones; the
    // close button is sized in on-screen pixels, so it needs that factor.
    const scale = form.offsetWidth ? form.getBoundingClientRect().width / form.offsetWidth : 1;
    if (host && scale > 0) host.style.setProperty("--ss-mc-scale", String(Math.round(scale * 1000) / 1000));
  }

  function repaintCover(node, state) {
    node.querySelectorAll(".ss-mc__card").forEach((button, index) => {
      if (state.turned.includes(index)) paintCard(button, index, true, false);
    });
  }

  function syncForm(form) {
    try {
      const existing = coverOf(form);
      if (existing?.dataset.revealing) return;
      const mystery = isMysteryStep(form);
      if (mystery || form.classList.contains(LOOK_CLASS)) applyLook(form);
      if (!mystery || loadState().unlocked) {
        if (existing) uncover(form);
        return;
      }
      if (existing) {
        setStepHidden(form, true);
        repaintCover(existing, loadState());
        fitFormToCover(form, existing);
        return;
      }
      if (cssState !== "unknown") return;
      cover(form, loadState());
    } catch {
      try {
        uncover(form);
      } catch {
        // Nothing left to undo.
      }
    }
  }

  function syncAll() {
    scheduled = false;
    for (const form of klaviyoForms()) {
      syncForm(form);
      watch(form);
    }
  }

  function scheduleSync() {
    if (scheduled) return;
    scheduled = true;
    win.requestAnimationFrame(syncAll);
  }

  // Klaviyo re-renders a step's DOM after its events, and may drop or replace
  // nodes we did not create; watching the popup root keeps the cover in step.
  function watch(form) {
    const root = form.closest('[role="dialog"]') || form.parentElement;
    if (!root || observers.has(root) || typeof win.MutationObserver !== "function") return;
    const observer = new win.MutationObserver(scheduleSync);
    observer.observe(root, { childList: true, subtree: true });
    observers.set(root, observer);
  }

  // When Klaviyo fires `open` before the form is in the DOM, watch the body
  // until it lands and sync from the observer callback itself: mutation
  // callbacks run before the next paint, so the email step is never seen
  // uncovered. Gives up after BODY_WATCH_MS.
  let bodyObserver = null;
  function watchBodyForForm() {
    if (bodyObserver || !doc.body || typeof win.MutationObserver !== "function") return;
    const observer = new win.MutationObserver(() => {
      if (!klaviyoForms().length) return;
      stopBodyWatch();
      syncAll();
    });
    observer.observe(doc.body, { childList: true, subtree: true });
    bodyObserver = observer;
    win.setTimeout(() => {
      if (bodyObserver === observer) stopBodyWatch();
    }, BODY_WATCH_MS);
  }
  function stopBodyWatch() {
    bodyObserver?.disconnect();
    bodyObserver = null;
  }

  win.addEventListener("klaviyoForms", (event) => {
    const type = event?.detail?.type;
    if (type !== "open" && type !== "viewedStep" && type !== "embedOpen") return;
    syncAll();
    if (!klaviyoForms().length) watchBodyForForm();
    scheduleSync();
    // Belt and braces for a step that renders after the event fires.
    for (const delay of [100, 400, 1200]) win.setTimeout(syncAll, delay);
  });

  // Another tab turning or unlocking cards updates any cover open here.
  win.addEventListener("storage", (event) => {
    if (event.key === STORAGE_KEY || event.key === null) scheduleSync();
  });

  // Klaviyo rescales the popup on resize and rotation.
  win.addEventListener("resize", scheduleSync);

  // A popup that opened before this deferred module ran has already fired
  // its events, so pick it up from the DOM.
  syncAll();
}

if (typeof window !== "undefined" && window.document) {
  try {
    install(window);
  } catch {
    // Never let the game break the page or the popup.
  }
}
